import React from 'react';
import PropTypes from 'prop-types';
import DashboardCard from './cards/DashboardCard';
import CardIconTile from '../../shared/components/CardIconTile';
import StatusPill from '../../shared/components/pill/StatusPill';
import LinkButton from '../../shared/components/LinkButton';
import ProgressStepDots from '../../shared/components/ProgressStepDots';

/**
 * OnboardingProgressCard
 * 
 * A compact card showing onboarding progress with a link to continue setup.
 * Displayed on the admin dashboard.
 * 
 * Props:
 *  - completedCount: Number of completed items
 *  - totalCount: Total number of items
 *  - onDismiss: Callback when user dismisses onboarding
 *  - className: Additional CSS classes for the container
 */
export default function OnboardingProgressCard({
  completedCount = 0,
  totalCount = 8,
  onDismiss,
  className = '',
}) {
  const isComplete = completedCount >= totalCount;

  return (
    <DashboardCard className={className} accent="purple">
      {/* Header with icon and badge */}
      <div className="d-flex align-items-start justify-content-between mb-3">
        <div className="d-flex align-items-center gap-3">
          <CardIconTile icon="bi-rocket-takeoff-fill" variant="purple" size="lg" />
          <div>
            <h6 className="fw-bold mb-0">Getting Started</h6>
            <span className="text-muted small">Set up your workspace</span>
          </div>
        </div>
        {isComplete ? (
          <StatusPill tone="success" icon="bi-check-lg">Complete</StatusPill>
        ) : (
          <StatusPill tone="progress" icon="bi-circle-half">In progress</StatusPill>
        )}
      </div>

      {/* Progress visualization */}
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <span className="small text-muted">Progress</span>
          <span className="small fw-semibold">{completedCount} of {totalCount}</span>
        </div>
        <ProgressStepDots
          completed={completedCount}
          total={totalCount}
          variant="green"
        />
      </div>

      {/* Action link */}
      <LinkButton
        to="/admin/welcome"
        icon="bi-arrow-right"
        variant="purple"
        className="w-100 mb-2"
      >
        {isComplete ? 'Review Setup' : 'Continue Setup'}
      </LinkButton>

      {/* Dismiss link */}
      {onDismiss && (
        <button
          type="button"
          className="btn btn-link btn-sm text-muted p-0 w-100"
          onClick={onDismiss}
        >
          Dismiss
        </button>
      )}
    </DashboardCard>
  );
}

OnboardingProgressCard.propTypes = {
  completedCount: PropTypes.number,
  totalCount: PropTypes.number,
  onDismiss: PropTypes.func,
  className: PropTypes.string,
};
